import { tmsEventCatalogOf } from "./base";
import { messageAcknowledged } from "./acknowledgement/messageAcknowledged";
import { verificationResponse } from "./acknowledgement/verificationResponse";
import { forecastLate } from "./appointment/forecastLate";
import { forecastOnTime } from "./appointment/forecastOnTime";
import { emptyCall } from "./call/emptyCall";
import { loadedCall } from "./call/loadedCall";
import { stopCall } from "./call/stopCall";
import { callOutOfSequence } from "./callRejection/callOutOfSequence";
import { dispatchLimitExceeded } from "./callRejection/dispatchLimitExceeded";
import { noAutoDispatch } from "./callRejection/noAutoDispatch";
import { notAtStop } from "./callRejection/notAtStop";
import { otherRejection } from "./callRejection/otherRejection";
import { trailerMismatch } from "./callRejection/trailerMismatch";
import { dailyCheckCall } from "./checkIn/dailyCheckCall";
import { dropSwapArrival } from "./checkIn/dropSwapArrival";
import { logCertified } from "./compliance/logCertified";
import { driving } from "./dutyStatus/driving";
import { offDuty } from "./dutyStatus/offDuty";
import { onDuty } from "./dutyStatus/onDuty";
import { sleeperBerth } from "./dutyStatus/sleeperBerth";
import { equipmentInventory } from "./equipment/equipmentInventory";
import { accidentReport } from "./exception/accidentReport";
import { fuelCardError } from "./exception/fuelCardError";
import { loadRefused } from "./exception/loadRefused";
import { osdReport } from "./exception/osdReport";
import { tollPassIssue } from "./exception/tollPassIssue";
import { fuelStop } from "./fuel/fuelStop";
import { breakOverdue } from "./hoursOfService/breakOverdue";
import { cycleLimitExceeded } from "./hoursOfService/cycleLimitExceeded";
import { drivingLimitExceeded } from "./hoursOfService/drivingLimitExceeded";
import { dutyWindowExceeded } from "./hoursOfService/dutyWindowExceeded";
import { dvir } from "./inspection/dvir";
import { arrival } from "./movement/arrival";
import { departure } from "./movement/departure";
import { advanceRequest } from "./request/advanceRequest";
import { fuelRequest } from "./request/fuelRequest";
import { loadHomeRequest } from "./request/loadHomeRequest";
import { routeAccepted } from "./routeAssignment/routeAccepted";
import { routeDeclined } from "./routeAssignment/routeDeclined";
import { login } from "./session/login";
import { logout } from "./session/logout";
import { personalConveyance } from "./specialDriving/personalConveyance";
import { specialDrivingEnded } from "./specialDriving/specialDrivingEnded";
import { yardMove } from "./specialDriving/yardMove";
import { trainerDailyForm } from "./training/trainerDailyForm";
import { trainingReport } from "./training/trainingReport";
import { other } from "./uncategorized/other";
import { vehicleAttached } from "./vehicleAssignment/vehicleAttached";
import { vehicleDetached } from "./vehicleAssignment/vehicleDetached";

/**
 * The vocabulary, declared exactly once: which kinds exist and which category each sits under.
 *
 * Placement only. Nothing is constructed here and no field is named here — a kind's schemas are built in its own
 * file, and everything downstream (the category and kind enums, the kind → category table, the unions, every lookup)
 * is reflected off this map in `taxonomy.ts`. Adding a kind is a file under its category's directory and a name in
 * the record below; a kind cannot be given the wrong category, because {@link tmsEventCatalogOf} refuses any leaf
 * whose own triple disagrees with the path it is placed at.
 *
 * The directory tree mirrors this map exactly, one directory per key and one file per kind, and a test walks the
 * tree to prove it: an orphaned kind file and a placement with no file both fail.
 *
 * Ordered for reading, and the order is a rule rather than an accident: subjects alphabetical, categories alphabetical
 * within a subject, and kinds alphabetical within a category. A subject's kinds are declared in
 * `subjects/<subject>/facts.ts`, and the registry, not this map, decides what a subject may state; the headings below
 * only say which subject each category belongs to.
 */
export const tmsEventCatalog = tmsEventCatalogOf({
  // ── DutyStatusChange ──
  // The driver attesting to the day's log.
  compliance: { logCertified },
  // The four HOS duty statuses a driver moves between.
  dutyStatus: { driving, offDuty, onDuty, sleeperBerth },
  // The driver signing in to or out of a unit's ELD.
  session: { login, logout },
  // Driving with the HOS rules set aside — personal conveyance, yard moves — and the end of either.
  specialDriving: { personalConveyance, specialDrivingEnded, yardMove },

  // ── FormSubmission ──
  // The driver answering something dispatch sent.
  acknowledgement: { messageAcknowledged, verificationResponse },
  // The driver reporting in, on schedule or on arrival.
  checkIn: { dailyCheckCall, dropSwapArrival },
  // The driver's inventory of what is on the unit.
  equipment: { equipmentInventory },
  // The driver reporting something that went wrong.
  exception: { accidentReport, fuelCardError, loadRefused, osdReport, tollPassIssue },
  // The driver asking dispatch for something.
  request: { advanceRequest, fuelRequest, loadHomeRequest },
  // Reports from and about drivers in training.
  training: { trainerDailyForm, trainingReport },
  // The reserved key: a form the vocabulary declines to classify, labeled by the vendor's own title.
  uncategorized: { other },

  // ── HosClockViolation ──
  // An HOS clock running out.
  hoursOfService: { breakOverdue, cycleLimitExceeded, drivingLimitExceeded, dutyWindowExceeded },

  // ── RouteAssignmentEvent ──
  // The driver's answer to a route assignment.
  routeAssignment: { routeAccepted, routeDeclined },

  // ── StopEvent ──
  // Storyboard's own forecast crossing or recrossing a stop's appointment.
  appointment: { forecastLate, forecastOnTime },
  // The driver's call at a stop, by what happened to the trailer.
  call: { emptyCall, loadedCall, stopCall },
  // The TMS refusing a call, by family of reason. Stated by StopEvent because a refusal has the same references as
  // the completion it stands in for; a vendor's own codes map onto the families in its adapter.
  callRejection: {
    callOutOfSequence,
    dispatchLimitExceeded,
    noAutoDispatch,
    notAtStop,
    otherRejection,
    trailerMismatch,
  },
  // The driver fuelling the unit.
  fuel: { fuelStop },
  // The driver's vehicle inspection report.
  inspection: { dvir },
  // The unit arriving at or leaving a stop.
  movement: { arrival, departure },

  // ── VehicleDriverAssignmentTransition ──
  // A driver taking or leaving a seat on a unit; an attach names the seat, a detach closes whatever was open.
  vehicleAssignment: { vehicleAttached, vehicleDetached },
});
